import * as React from 'react';

import { Formatters } from '../../../codegen/Localize';
import { sanitize } from '../../../commonInfra/utils/Sanitize';
import { getContentSpaceDefinition } from '../../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { DraftsById } from '../../../drafts/types/DraftsTypes';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { L10n } from '../../../ts/L10n/L10n';
import { isRecommendationType, isSpaceInfoItemType } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export interface ContentCardProps extends SiteUrlsProps {
  readonly content: IContentInfo;
  readonly selected: boolean;
  readonly hoveredOrSelected?: boolean;
  readonly forDragPreview: boolean;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly getContentDetail: (content: IContentInfo) => void;
  readonly inWorkbookDatasourcesSubspace: boolean;
  readonly inDatasourceConnectionsSubspace: boolean;
  readonly inProjectSpace?: boolean;
  readonly inRecentsSpace?: boolean;
  readonly inCollectionSpace?: boolean;  // TODO remove with TFS 1102281
  readonly inPersonalSpace?: boolean;
  readonly allowDragAndDrop: boolean;
  readonly showActionMenu: boolean;
  readonly showLastAccessedAt?: boolean;
  readonly disableTooltip: boolean;
  readonly drafts?: DraftsById;
}

export const maxDescriptionLengthWithDraft = 48;

export abstract class ContentCard<Props extends ContentCardProps = ContentCardProps, State = {}>
  extends React.PureComponent<Props, State> {

    protected getDescription() {
      return TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
    }

    protected getDescriptionWithDraft() {
    let descriptionPlaintext = TextileHelper.getPlainTextFromTextile(this.props.content.description, sanitize);
    return L10n.elideIfLong(descriptionPlaintext, maxDescriptionLengthWithDraft);
    }

    protected getLastAccessedAt() {
      return this.props.content.lastAccessedAt ? Formatters.formatLongBestFitRelativeTime(new Date(this.props.content.lastAccessedAt)) : '';
    }

    protected renderActionsMenu(): JSX.Element {
      return this.props.showActionMenu &&
        <div style={{paddingTop: '2px'}}>
          <ActionsMenu
            targetItems={[ this.props.content ]}
            disabled={ this.props.selected }
            useIcon={true}/>
        </div>;
    }

    protected handleShowTooltip = () => {
      this.props.getContentDetail(this.props.content);
    }

    protected showFavoritesStar = () => {  // TODo: remove with TFS 1102281
      if (!FeatureFlags.useCollectionsFavorites) {
        return !this.props.inCollectionSpace;
      }

      return true;
    }
}

export function getContentUrlForItem(content: IContentInfo, urls: SpacesSiteUrls): string | undefined {
  let contentType = content.contentType;
  if (isRecommendationType(contentType)) {
    contentType = 'view';
  }

  // it only makes sense to generate a url for items that have their own space
  // and hence are SpaceInfoItemType('s)
  if (isSpaceInfoItemType(contentType)) {
    const contentDefinition = getContentSpaceDefinition(contentType);

    return contentDefinition.contentSpaceUrl(urls, content.id, content.luid);
  }
}
